import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Coins, Eye, Gift, History, Loader2, Lock, PenLine } from 'lucide-react';
import api from '../../services/api';
import { getParcoursActif } from '../../services/parcoursActif';
import QrcVerdictView from './QrcVerdictView';
import QrcAttemptModal from './QrcAttemptModal';
import QrcPackCheckoutModal from '../stripe/QrcPackCheckoutModal';
import { QrcShowcase, qrcForceCredits } from './QrcPlayer';
import useQrcGrading from './useQrcGrading';
import { scrollToPanel } from './QrcSpotlight';

const MOBILE_QUERY = '(max-width: 899px)';
const isMobile = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
const prefersReduce = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Découpe en phrases (sans lookbehind : Safari < 16.4). Les séparateurs restent attachés. */
const sentencesOf = (text) => (text || '').match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [text || ''];

/**
 * Titre de repli dans la liste quand la question n'a pas de titre court : la première
 * phrase de l'énoncé, abrégée au mot près.
 */
export function shortTitle(enonce) {
    const first = sentencesOf(enonce)[0].trim();
    if (first.length <= 72) return first;
    const cut = first.slice(0, 68);
    const sp = cut.lastIndexOf(' ');
    return (sp > 30 ? cut.slice(0, sp) : cut) + '…';
}

/**
 * Énoncé de l'espace de travail : le récit d'un cas pratique en texte normal, la consigne
 * (à partir de la première phrase interrogative, sinon la dernière phrase d'un long énoncé)
 * en gras — un cas pratique tout en gras occupait l'écran d'un téléphone (revue Astra).
 */
function Enonce({ text }) {
    const parts = sentencesOf(text);
    let split = parts.findIndex(p => p.trim().endsWith('?'));
    if (split <= 0 && parts.length > 2 && (text || '').length > 220) split = parts.length - 1;
    const ctx = split > 0 ? parts.slice(0, split).join('').trim() : '';
    const consigne = split > 0 ? parts.slice(split).join('').trim() : (text || '').trim();
    return (
        <p className="qrcw-enonce">
            {ctx && <span className="qrcw-enonce-ctx">{ctx}</span>}
            <strong>{consigne}</strong>
        </p>
    );
}

const FILTRES = [
    { k: 'all', label: 'Toutes' },
    { k: 'todo', label: 'À faire' },
    { k: 'redo', label: 'À retravailler' },
    { k: 'ok', label: 'Acquises' },
];

/**
 * Atelier « Répondre à des questions courtes » (onglet Rédaction, 29/09/2026) : sur le
 * modèle du bac à sable admin, une liste des questions à gauche (groupées par sujet,
 * état par question) et, à droite, la question active en grand avec la zone de rédaction,
 * puis le verdict. Sur mobile, la liste et l'atelier se relaient (« Toutes les questions »).
 *
 * Brouillon et état de correction sont tenus PAR QUESTION : changer de question, ou cliquer
 * la question active, n'efface rien, et un verdict arrivé pendant qu'on lit une autre
 * question reste attaché à la question soumise (revue Astra, 29/09/2026).
 *
 * `readOnly` (visiteur) : énoncés via l'endpoint public `qrc/showcase.php` ; la question
 * démo montre ses exemples corrigés, les autres une zone verrouillée avec l'inscription.
 * Inscrit sans abonnement : même chose, avec l'abonnement en appel à l'action.
 * Abonné (ou admin) : rédaction, correction (mode crédits : solde + recharge), verdict.
 */
export default function QrcWorkshop({
    scope, sujets, user, hasAccess, accentColor = null, readOnly = false,
    onOpenSujet = null, onSubscribe = null,
}) {
    const [data, setData] = useState(null);         // null = chargement, [] = aucune
    const [meta, setMeta] = useState({});           // policy, credits, is_admin, credits_available
    const [currentId, setCurrentId] = useState(null);
    const [drafts, setDrafts] = useState({});       // qid → brouillon
    const [stageMode, setStageMode] = useState(false); // mobile : liste ↔ atelier
    const [viewAttemptId, setViewAttemptId] = useState(null);
    const [buyOpen, setBuyOpen] = useState(false);
    const [filtre, setFiltre] = useState('all');
    const [force] = useState(qrcForceCredits());
    // Le parcours actif fait partie de l'identité du chargement : changer de
    // bouton doit redemander la liste, pas seulement la réordonner.
    const parcours = getParcoursActif().slug;
    const stageRef = useRef(null);

    const load = useCallback(() => {
        if (readOnly) {
            return api.get(`/qrc/showcase.php?scope_slug=${encodeURIComponent(scope)}`)
                .then(({ data: d }) => {
                    const x = d.data || {};
                    const demoById = Object.fromEntries((x.demo || []).map(q => [q.id, q]));
                    setData((x.questions || []).map(q => ({ ...q, accessible: false, samples: demoById[q.id]?.samples || null })));
                    setMeta({ policy: x.scope?.qrc_policy });
                })
                .catch(() => setData([]));
        }
        const params = new URLSearchParams({ scope_slug: scope, all: '1' });
        if (force) params.set('policy', 'credits');
        return api.get(`/qcm/qrc-list.php?${params}`)
            .then(({ data: d }) => {
                const x = d.data || {};
                setData(x.qrc || []);
                setMeta({ policy: x.policy, credits: x.credits, is_admin: !!x.is_admin, credits_available: !!x.credits_available });
            })
            .catch(() => setData([]));
    }, [scope, readOnly, force, parcours]);
    useEffect(() => { load(); }, [load]);

    const grader = useQrcGrading({
        scope, force,
        onCredits: (c) => setMeta(m => ({ ...m, credits: c })),
        onGraded: () => load(), // rafraîchit l'état (tentée / rejouable / solde)
    });

    // Groupes par sujet, dans l'ordre des catégories de la page
    const groups = useMemo(() => {
        if (!data) return [];
        const bySujet = new Map();
        data.forEach(q => {
            const k = q.sous_sujet_id;
            if (!bySujet.has(k)) bySujet.set(k, []);
            bySujet.get(k).push(q);
        });
        const ordered = [];
        (sujets || []).forEach(ss => {
            if (!bySujet.has(ss.id)) return;
            ordered.push({ id: ss.id, label: ss.label, matiere: ss.matiere_label || null, questions: bySujet.get(ss.id) });
            bySujet.delete(ss.id);
        });
        bySujet.forEach((qs, id) => ordered.push({ id, label: qs[0].sujet_label || 'Sujet', matiere: qs[0].matiere_label || null, questions: qs }));
        return ordered;
    }, [data, sujets]);
    const flatBrut = useMemo(() => groups.flatMap(g => g.questions.map(q => ({ ...q, group: g }))), [groups]);
    const engineOpen = !readOnly && flatBrut.some(q => q.accessible);
    // Sans moteur ouvert (visiteur, ou accès sans crédit), on met en avant la
    // première question qui porte des exemples corrigés : c'est par là qu'on juge.
    const pinned = engineOpen ? null : (flatBrut.find(q => Array.isArray(q.samples) && q.samples.length > 0) || null);
    // L'ordre courant la place en premier : numéros, compteur et navigation en découlent.
    const flat = useMemo(() => (pinned
        ? [flatBrut.find(q => q.id === pinned.id), ...flatBrut.filter(q => q.id !== pinned.id)]
        : flatBrut), [flatBrut, pinned]);
    const index = flat.findIndex(q => q.id === currentId);
    const current = index >= 0 ? flat[index] : null;

    // Bureau : une question est ouverte d'emblée (la démo pour qui n'a pas le moteur,
    // sinon la première à faire). Mobile : la liste d'abord.
    useEffect(() => {
        // La question ouverte peut ne plus faire partie du parcours choisi : on
        // reprend alors la première encore visible, sans toucher aux brouillons.
        if (currentId !== null && flat.length && !flat.some(q => q.id === currentId)) {
            setCurrentId(flat[0].id);
            return;
        }
        if (!flat.length || currentId !== null || isMobile()) return;
        const pick = flat.find(q => !q.accessible && Array.isArray(q.samples) && q.samples.length > 0)
            || flat.find(q => q.accessible && !q.attempted)
            || flat[0];
        setCurrentId(pick.id);
    }, [flat, currentId]);

    // Changer de question ne touche ni aux brouillons ni aux verdicts ; cliquer la question
    // active ne fait que revenir à l'atelier (mobile).
    const select = (q) => {
        if (!q) return;
        if (q.id !== currentId) setCurrentId(q.id);
        setStageMode(true);
        if (isMobile()) requestAnimationFrame(() => scrollToPanel(stageRef.current, prefersReduce()));
    };

    if (data === null) return <div className="qrcw-loading"><Loader2 size={16} className="spin" /> Chargement des questions…</div>;
    if (!flat.length) return null;

    const creditsMode = meta.policy === 'credits';
    const balance = meta.credits?.balance ?? null;
    const noCredit = creditsMode && balance !== null && balance <= 0;
    const done = flat.filter(q => q.attempted).length;
    const acquired = flat.filter(q => q.last_passed).length;
    const plural = (n, w) => `${n} ${w}${n > 1 ? 's' : ''}`;
    const answer = current ? (drafts[current.id] || '') : '';
    const setAnswer = (v) => { if (current) setDrafts(d => ({ ...d, [current.id]: v })); };

    // Le moteur est-il ouvert à cette personne ? Sinon l'accès est dit une fois et la
    // démonstration sort du flux, au lieu d'un cadenas sur chaque ligne (design « parcours »).

    /** État d'une question : le verdict qui vient d'arriver prime sur la liste, rechargée juste après. */
    const stateOf = (q) => {
        if (!engineOpen) return 'todo';
        const v = grader.get(q.id).verdict;
        if (v) return (v.passed === 1 || v.passed === true) ? 'ok' : 'redo';
        if (!q.attempted) return 'todo';
        return q.last_passed ? 'ok' : 'redo';
    };
    const scoreOf = (q) => {
        const v = grader.get(q.id).verdict;
        if (v) return v.score_core_pct ?? v.score_pct;
        return q.attempted ? q.last_score : null;
    };
    const matchFiltre = (q) => filtre === 'all' || stateOf(q) === filtre;

    /** Une ligne : rail d'état à gauche, titre, et à droite un score seulement s'il existe. */
    const renderRow = (q) => {
        const n = flat.findIndex(x => x.id === q.id) + 1;
        const on = q.id === currentId;
        const st = grader.get(q.id);
        const state = stateOf(q);
        const score = scoreOf(q);
        return (
            <li key={q.id}>
                <button
                    type="button" className={`qrcw-item is-${state}`} onClick={() => select(q)}
                    aria-current={on ? 'true' : undefined} title={q.enonce}
                >
                    <span className="qrcw-item-n">{String(n).padStart(2, '0')}</span>
                    <span className="qrcw-item-t">{q.titre || shortTitle(q.enonce)}</span>
                    {st.grading ? <span className="qrcw-item-busy"><Loader2 size={10} className="spin" /> Correction…</span>
                        : q.status === 'EDITION' ? <span className="qrcw-item-draft">Brouillon</span>
                            : (score !== null && score !== undefined) ? <span className={`qrcw-item-score is-${state}`}>{score} %</span>
                                : null}
                    <ChevronRight size={14} className="qrcw-item-chev" />
                </button>
            </li>
        );
    };

    const renderBody = () => {
        const q = current;
        const st = grader.get(q.id);
        const hasSamples = Array.isArray(q.samples) && q.samples.length > 0;
        const showcase = !q.accessible && hasSamples;
        if (showcase || readOnly || !q.accessible) {
            return (
                <>
                    {showcase && <div className="qrcw-showcase"><QrcShowcase key={q.id} q={q} noCta /></div>}
                    {!showcase && (
                        <div className="qrcw-fake" aria-hidden="true"><span>Rédige ta réponse en quelques phrases…</span></div>
                    )}
                    <div className="qrcw-locked-msg">
                        <Lock size={16} />
                        <p>
                            {readOnly
                                ? 'Avec un compte et un abonnement, tu rédiges ta propre réponse ici et le correcteur te rend ton verdict point par point.'
                                : 'Avec un abonnement, tu rédiges ta propre réponse ici et le correcteur te rend ton verdict point par point.'}
                        </p>
                        {readOnly
                            ? <Link to="/inscription" className="qrcw-cta">Créer mon compte gratuit</Link>
                            : onSubscribe && <button type="button" className="qrcw-cta" onClick={onSubscribe}>M'abonner pour rédiger</button>}
                    </div>
                </>
            );
        }
        const locked = q.attempted && !q.replayable;
        return (
            <>
                {/* Contrôle admin : la vitrine telle que la voit un non-abonné */}
                {hasSamples && (
                    <details className="qrcp-admin-vitrine">
                        <summary><Eye size={13} /> Vitrine démo — vue non-abonné ({plural(q.samples.length, 'exemple')})</summary>
                        <div className="qrcp-admin-vitrine-body"><QrcShowcase key={q.id} q={q} isPreview /></div>
                    </details>
                )}
                {!st.verdict ? (
                    <>
                        {locked && <p className="qrcw-note"><Lock size={13} /> {q.blocked_reason}</p>}
                        {noCredit && !locked && (
                            <p className="qrcw-note">
                                <Coins size={13} />
                                <span>
                                    Ton solde de corrections est à zéro : ton prochain abonnement en ajoutera, ou recharge dès 3 € (ce que tu n'utilises pas ne se perd jamais).
                                    {' '}<button type="button" onClick={() => setBuyOpen(true)}>Recharger des corrections</button>
                                </span>
                            </p>
                        )}
                        {q.attempted && q.last_attempt_id && (
                            <button type="button" className="qrcw-last" onClick={() => setViewAttemptId(q.last_attempt_id)}>
                                <History size={12} /> Voir ma dernière correction ({q.last_score} %)
                            </button>
                        )}
                        <textarea
                            value={answer} maxLength={5000}
                            onChange={e => { setAnswer(e.target.value); grader.touch(q.id); }}
                            placeholder="Rédige ta réponse en quelques phrases…"
                            disabled={locked || noCredit || st.grading}
                            aria-label="Ta réponse"
                        />
                        <div className="qrcw-tools">
                            <span>Conseil : une idée par phrase, avec les mots du programme.</span>
                            <span>{plural(answer.length, 'caractère')}</span>
                        </div>
                        {st.err && <p className="qrcw-err">{st.err}</p>}
                        <div className="qrcw-submit">
                            <button
                                className="btn btn-primary"
                                onClick={() => grader.submit(q, answer, st.pendingKey)}
                                disabled={st.grading || !answer.trim() || locked || noCredit}
                            >
                                {st.grading ? <><Loader2 size={14} className="spin" /> Correction…</>
                                    : st.pendingKey ? 'Récupérer ma correction'
                                        : creditsMode ? 'Corriger ma réponse (1 correction)' : 'Corriger ma réponse'}
                            </button>
                            {st.grading && <span className="qrcw-tools">Le correcteur lit ta réponse point par point, quelques secondes. Tu peux consulter une autre question en attendant.</span>}
                        </div>
                    </>
                ) : (
                    <>
                        {st.answer && (
                            <details className="qrcw-sent">
                                <summary><PenLine size={12} /> Ta réponse · {plural(st.answer.trim().split(/\s+/).length, 'mot')}</summary>
                                <p>{st.answer}</p>
                            </details>
                        )}
                        <QrcVerdictView verdict={st.verdict} onReplay={() => { grader.reset(q.id); setAnswer(''); }} />
                        <div className="qrcw-next">
                            {onOpenSujet && (
                                <>
                                    <span className="qrcw-next-k">Pour progresser</span>
                                    {/* Chapitres à travailler (migration 146) ; à défaut, le sujet d'appartenance */}
                                    {(q.revisions?.length ? q.revisions : [{ id: q.group.id, label: q.group.label }]).map(r => (
                                        <button key={r.id} type="button" className="qrcw-chip" onClick={() => onOpenSujet(r.id)}>Quiz · {r.label}</button>
                                    ))}
                                </>
                            )}
                            {index < flat.length - 1 && (
                                <button type="button" className="qrcw-chip is-primary" onClick={() => select(flat[index + 1])}>Question suivante <ChevronRight size={13} /></button>
                            )}
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <section className={`qrcw ${stageMode ? 'is-stage' : ''}`} style={accentColor ? { '--qrcw-accent': accentColor } : undefined}>
            <aside className="qrcw-nav" aria-label="Questions à réponse rédigée">
                <div className="qrcw-nav-head">
                    <span className="qrcw-nav-ic"><PenLine size={16} /></span>
                    <div>
                        <strong>{plural(flat.length, 'question')} courte{flat.length > 1 ? 's' : ''}</strong>
                        <small>
                            {plural(groups.length, 'sujet')}
                            {engineOpen && done > 0 && <> · {plural(done, 'rédigée')}{acquired > 0 && <>, {plural(acquired, 'acquise')}</>}</>}
                        </small>
                    </div>
                </div>
                {engineOpen && done > 0 && flat.length > 1 && (
                    <div className="qrcw-progress" aria-hidden="true"><i style={{ width: `${Math.round(done / flat.length * 100)}%` }} /></div>
                )}

                {/* L'accès est dit une fois, au lieu d'un cadenas sur chaque ligne */}
                {!engineOpen && (
                    <p className="qrcw-access">
                        <Lock size={15} />
                        <span>
                            Tu peux lire toutes les questions. Pour rédiger et recevoir ta correction,{' '}
                            {readOnly
                                ? <><Link to="/inscription">crée ton compte</Link> puis abonne-toi.</>
                                : <><button type="button" onClick={onSubscribe}>abonne-toi</button>.</>}
                        </span>
                    </p>
                )}

                {/* La démonstration sort du flux tant que le moteur n'est pas ouvert */}
                {pinned && (
                    <div className="qrcw-pinned">
                        <span><Gift size={12} /> Commence par là</span>
                        <ol className="qrcw-items">{renderRow(pinned)}</ol>
                        <p>{plural(pinned.samples.length, 'réponse')} d'exemple, corrigée{pinned.samples.length > 1 ? 's' : ''} point par point.</p>
                    </div>
                )}

                {/* Filtres : utiles dès que l'historique se garnit */}
                {engineOpen && done >= 3 && (
                    <div className="qrcw-filters" role="group" aria-label="Filtrer les questions">
                        {FILTRES.map(f => {
                            const nb = f.k === 'all' ? flat.length : flat.filter(q => stateOf(q) === f.k).length;
                            if (!nb && f.k !== 'all') return null;
                            return (
                                <button key={f.k} type="button" aria-pressed={filtre === f.k} onClick={() => setFiltre(f.k)}>
                                    {f.label}<em>{nb}</em>
                                </button>
                            );
                        })}
                    </div>
                )}

                {groups.map(g => {
                    const visibles = g.questions.filter(q => q.id !== pinned?.id && matchFiltre(q));
                    if (!visibles.length) return null;
                    // À la place du libellé de matière, répété et tronqué : l'avancement du sujet
                    const faits = g.questions.filter(q => q.attempted).length;
                    const ok = g.questions.filter(q => stateOf(q) === 'ok').length;
                    return (
                        <div className="qrcw-group" key={g.id}>
                            <div className="qrcw-group-h">
                                <span className="qrcw-group-label">{g.label}</span>
                                {engineOpen && faits > 0 && (
                                    <em className={ok === g.questions.length ? 'is-done' : ''}>{ok}/{g.questions.length}</em>
                                )}
                            </div>
                            <ol className="qrcw-items">{visibles.map(renderRow)}</ol>
                        </div>
                    );
                })}
                {filtre !== 'all' && !flat.some(q => q.id !== pinned?.id && matchFiltre(q)) && (
                    <p className="qrcw-nofilter">Aucune question dans ce filtre.</p>
                )}
            </aside>

            <div className="qrcw-stage" ref={stageRef}>
                {!current ? (
                    <div className="qrcw-empty"><PenLine size={22} /><p>Choisis une question dans la liste pour commencer.</p></div>
                ) : (
                    <>
                        <div className="qrcw-stage-top">
                            <button type="button" className="qrcw-back" onClick={() => setStageMode(false)}><ChevronLeft size={15} /> Toutes les questions</button>
                            <span className="qrcw-crumb">{current.group.matiere ? `${current.group.matiere} · ` : ''}{current.group.label}</span>
                            {creditsMode && balance !== null && (
                                <span className={`qrcw-solde ${balance > 0 ? 'ok' : 'ko'}`} title="Ton solde de corrections : versé par chaque abonnement selon sa durée, jamais perdu">
                                    <Coins size={12} /> Solde : {plural(balance, 'correction')}
                                    <button type="button" onClick={() => setBuyOpen(true)}>Recharger</button>
                                </span>
                            )}
                            {/* Précédente / Suivante en tête, toujours au même endroit (demande Fabien, 29/09/2026) */}
                            <nav className="qrcw-pager" aria-label="Naviguer entre les questions">
                                <button type="button" className="qrcw-navbtn" disabled={index <= 0} onClick={() => select(flat[index - 1])} title="Question précédente"><ChevronLeft size={15} /><span>Précédente</span></button>
                                <span className="qrcw-pos">Question {index + 1} / {flat.length}</span>
                                <button type="button" className="qrcw-navbtn" disabled={index >= flat.length - 1} onClick={() => select(flat[index + 1])} title="Question suivante"><span>Suivante</span><ChevronRight size={15} /></button>
                            </nav>
                        </div>
                        <Enonce text={current.enonce} />
                        {(current.status === 'EDITION' || (current.is_demo && !current.accessible) || current.attempted) && (
                            <div className="qrcw-tags">
                                {current.status === 'EDITION' && <span className="qrcw-pill draft">Brouillon</span>}
                                {!!Number(current.is_demo) && !current.accessible && <span className="qrcw-pill demo"><Gift size={11} /> Exemple offert</span>}
                                {current.attempted && <span className={`qrcw-pill ${current.last_passed ? 'ok' : 'ko'}`}>Dernière correction : {current.last_score} %</span>}
                            </div>
                        )}
                        {renderBody()}
                    </>
                )}
            </div>
            {viewAttemptId && <QrcAttemptModal attemptId={viewAttemptId} onClose={() => setViewAttemptId(null)} />}
            {buyOpen && <QrcPackCheckoutModal onClose={() => setBuyOpen(false)} onSuccess={() => { setBuyOpen(false); load(); }} />}
        </section>
    );
}
